import { NativeButton as PermissionNativeButton } from 'components/ui/PermissionActions';
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import io from 'socket.io-client';

import ArrowLeftOutlined from '@ant-design/icons/ArrowLeftOutlined';
import MessageOutlined from '@ant-design/icons/MessageOutlined';
import TeamOutlined from '@ant-design/icons/TeamOutlined';
import CopyOutlined from '@ant-design/icons/CopyOutlined';
import CheckCircleOutlined from '@ant-design/icons/CheckCircleOutlined';
import PhoneOutlined from '@ant-design/icons/PhoneOutlined';
import SendOutlined from '@ant-design/icons/SendOutlined';
import CloseOutlined from '@ant-design/icons/CloseOutlined';
import SearchOutlined from '@ant-design/icons/SearchOutlined';
import LoadingOutlined from '@ant-design/icons/LoadingOutlined';
import VideoCameraOutlined from '@ant-design/icons/VideoCameraOutlined';
import AudioOutlined from '@ant-design/icons/AudioOutlined';
import AudioMutedOutlined from '@ant-design/icons/AudioMutedOutlined';
import DesktopOutlined from '@ant-design/icons/DesktopOutlined';
import SafetyCertificateOutlined from '@ant-design/icons/SafetyCertificateOutlined';
import DeleteOutlined from '@ant-design/icons/DeleteOutlined';
import ExpandOutlined from '@ant-design/icons/ExpandOutlined';
import MinusOutlined from '@ant-design/icons/MinusOutlined';
import UpOutlined from '@ant-design/icons/UpOutlined';
import HolderOutlined from '@ant-design/icons/HolderOutlined';

import {
  fetchMeetingByRoomId,
  startMeeting,
  endMeeting,
  leaveMeetingRoom,
  fetchMeetingInvitees
} from 'api/meetings';
import { useMeeting } from 'contexts/MeetingContext';
import { getSocketBaseUrl } from 'utils/socketUrl';

import './MeetingRoom.css';

const ICE_SERVERS = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
    { urls: 'stun:stun2.l.google.com:19302' }
  ]
};

// Sub-component for individual participant video tile
function VideoTile({
  stream,
  userName,
  isLocal = false,
  isAudioMuted = false,
  isVideoOff = false,
  isScreenShare = false
}) {
  const videoRef = useRef(null);

  // Live video is considered available if camera is not marked off (or is screen share), stream exists, and has video tracks
  const hasLiveVideo = Boolean(
    (!isVideoOff || isScreenShare) &&
    stream &&
    stream.getVideoTracks &&
    stream.getVideoTracks().length > 0 &&
    (isLocal ? stream.getVideoTracks().some((t) => t.enabled) : true)
  );

  useEffect(() => {
    const el = videoRef.current;
    if (el && stream) {
      if (el.srcObject !== stream) {
        el.srcObject = stream;
      }
      const playPromise = el.play();
      if (playPromise !== undefined) {
        playPromise.catch((err) => {
          console.warn('Video auto-play failed, attempting muted retry:', err);
          el.muted = true;
          el.play().catch(() => {});
        });
      }
    }
  }, [stream, isVideoOff, hasLiveVideo, isScreenShare]);

  const initials = (userName || 'U')
    .trim()
    .split(' ')
    .filter(Boolean)
    .map((n) => n[0])
    .join('')
    .substring(0, 2)
    .toUpperCase() || 'U';

  return (
    <div className={`meet-video-tile ${!hasLiveVideo ? 'video-off' : ''} ${isScreenShare ? 'is-screen-share' : ''}`}>
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted={isLocal || isScreenShare}
        onLoadedMetadata={() => videoRef.current?.play().catch(() => {})}
        className={`meet-video-element ${hasLiveVideo ? 'video-active' : 'video-hidden'}`}
      />

      {!hasLiveVideo && (
        <div className="meet-avatar-fallback">
          <div className="meet-avatar-circle">{initials}</div>
          <span className="meet-avatar-name">{userName}</span>
        </div>
      )}

      <div className="meet-tile-footer">
        <span className="meet-tile-name">
          {userName} {isLocal ? '(You)' : ''}
        </span>
        <div className="meet-tile-badges">
          {isAudioMuted && (
            <span className="meet-tile-badge muted" title="Microphone Muted">
              <AudioMutedOutlined />
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export default function MeetingRoom({
  roomId: propRoomId,
  isMinimized: propMinimized,
  onMinimize: propOnMinimize,
  onMaximize: propOnMaximize,
  onClose: propOnClose
} = {}) {
  const { roomId: routeRoomId } = useParams();
  const roomId = propRoomId || routeRoomId;
  const navigate = useNavigate();

  const {
    isMinimized: contextMinimized,
    minimizeMeeting,
    maximizeMeeting,
    closeMeeting
  } = useMeeting();

  const isMinimized = propMinimized !== undefined ? propMinimized : contextMinimized;
  const handleMinimize = propOnMinimize || minimizeMeeting;
  const handleMaximize = propOnMaximize || maximizeMeeting;
  const handleClose = propOnClose || closeMeeting;

  // Minimized PiP display mode: full floating card vs ultra-compact background audio pill
  const [isPipCollapsed, setIsPipCollapsed] = useState(false);
  // Draggable position state for the collapsed pill bar
  const [pillPos, setPillPos] = useState({ x: null, y: null });
  const [isDraggingPill, setIsDraggingPill] = useState(false);
  const dragStartRef = useRef({ mouseX: 0, mouseY: 0, startX: 0, startY: 0, hasMoved: false });
  const pillRef = useRef(null);

  const startPillDrag = (clientX, clientY) => {
    const el = pillRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    dragStartRef.current = {
      mouseX: clientX,
      mouseY: clientY,
      startX: rect.left,
      startY: rect.top,
      hasMoved: false
    };
    setIsDraggingPill(true);
  };

  const updatePillDrag = (clientX, clientY) => {
    const el = pillRef.current;
    if (!el) return;
    const deltaX = clientX - dragStartRef.current.mouseX;
    const deltaY = clientY - dragStartRef.current.mouseY;

    if (Math.abs(deltaX) > 4 || Math.abs(deltaY) > 4) {
      dragStartRef.current.hasMoved = true;
    }

    const width = el.offsetWidth || 380;
    const height = el.offsetHeight || 48;
    const maxX = Math.max(0, window.innerWidth - width - 8);
    const maxY = Math.max(0, window.innerHeight - height - 8);

    const newX = Math.min(Math.max(8, dragStartRef.current.startX + deltaX), maxX);
    const newY = Math.min(Math.max(8, dragStartRef.current.startY + deltaY), maxY);

    setPillPos({ x: newX, y: newY });
  };

  const handlePillMouseDown = (e) => {
    if (e.target.closest('button')) return;
    startPillDrag(e.clientX, e.clientY);

    const onMouseMove = (moveEvent) => {
      updatePillDrag(moveEvent.clientX, moveEvent.clientY);
    };

    const onMouseUp = () => {
      setIsDraggingPill(false);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  const handlePillTouchStart = (e) => {
    if (e.target.closest('button')) return;
    const touch = e.touches[0];
    if (!touch) return;
    startPillDrag(touch.clientX, touch.clientY);

    const onTouchMove = (moveEvent) => {
      const moveTouch = moveEvent.touches[0];
      if (moveTouch) {
        updatePillDrag(moveTouch.clientX, moveTouch.clientY);
      }
    };

    const onTouchEnd = () => {
      setIsDraggingPill(false);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
    };

    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend', onTouchEnd);
  };

  const handlePillClick = () => {
    if (dragStartRef.current.hasMoved) return;
    handleMaximize();
  };


  // User identity from localStorage (No dummy fallbacks)
  const currentUserName =
    localStorage.getItem('userName') ||
    localStorage.getItem('userEmail')?.split('@')[0] ||
    '';
  const currentUserEmail = localStorage.getItem('userEmail') || '';
  const currentEmployeeId = localStorage.getItem('employeeId') || '';
  const currentUserId = localStorage.getItem('userId') || '';
  const mediaPreferenceKey = `meetingMediaPreferences:${currentUserId || currentEmployeeId || currentUserName}`;
  const [savedMediaPreferences] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(mediaPreferenceKey) || '{}');
    } catch {
      return {};
    }
  });

  // Room & Meeting State
  const [meeting, setMeeting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [duration, setDuration] = useState(0);

  // Admission & Waiting Room State
  const [isAdmitted, setIsAdmitted] = useState(false);
  const [isWaitingForHost, setIsWaitingForHost] = useState(false);
  const [waitingHostName, setWaitingHostName] = useState('Host');
  const [waitingUsers, setWaitingUsers] = useState([]); // List of users knocking for admission (Host sees this)
  const [admissionNotification, setAdmissionNotification] = useState(null); // Active knock notification popup
  const [passcodeRequired, setPasscodeRequired] = useState(false);
  const [passcodeInput, setPasscodeInput] = useState('');

  // Local Media State
  const [localStream, setLocalStream] = useState(null);
  const [screenStream, setScreenStream] = useState(null);
  const [isAudioMuted, setIsAudioMuted] = useState(savedMediaPreferences.isAudioMuted ?? false);
  const [isVideoOff, setIsVideoOff] = useState(savedMediaPreferences.isVideoOff ?? false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [mediaError, setMediaError] = useState(null);

  const isAudioMutedRef = useRef(isAudioMuted);
  const isVideoOffRef = useRef(isVideoOff);

  // Remote Peers: map of socketId -> { socketId, userName, employeeId, stream, isAudioMuted, isVideoOff, isScreenSharing }
  const [peers, setPeers] = useState({});
  const remotePeerList = Object.values(peers);

  useEffect(() => {
    isAudioMutedRef.current = isAudioMuted;
    isVideoOffRef.current = isVideoOff;
    localStorage.setItem(mediaPreferenceKey, JSON.stringify({ isAudioMuted, isVideoOff }));
  }, [mediaPreferenceKey, isAudioMuted, isVideoOff]);

  // Drawers & Chat
  const [activeDrawer, setActiveDrawer] = useState(null); // 'chat' | 'participants' | null
  const [chatMessages, setChatMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [sendingChat, setSendingChat] = useState(false);
  const [participantsList, setParticipantsList] = useState([]);
  const [unreadChatCount, setUnreadChatCount] = useState(0);
  const [availableInvitees, setAvailableInvitees] = useState([]);
  const [pullSearch, setPullSearch] = useState('');
  const [callingUsersMap, setCallingUsersMap] = useState({});
  const [declinedToast, setDeclinedToast] = useState(null);

  // Refs for WebRTC & Sockets
  const socketRef = useRef(null);
  const localStreamRef = useRef(null);
  const screenStreamRef = useRef(null);
  const peerConnectionsRef = useRef({}); // socketId -> RTCPeerConnection
  const candidateQueueRef = useRef({}); // socketId -> RTCIceCandidate[]
  const chatBottomRef = useRef(null);
  const isLeavingMeetingRef = useRef(false);

  // Guard against accidental tab closure, refresh, or browser navigation during active call
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isLeavingMeetingRef.current) return;
      e.preventDefault();
      e.returnValue = 'You are currently inside an active meeting. Leaving will disconnect you.';
      return e.returnValue;
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // Auto-dismiss call declined toast notification after 4 seconds
  useEffect(() => {
    if (!declinedToast) return;
    const timer = setTimeout(() => {
      setDeclinedToast(null);
    }, 4000);
    return () => clearTimeout(timer);
  }, [declinedToast]);

  // Web Audio keepalive: prevents Chrome/Edge from background throttling, suspending or killing WebSockets
  useEffect(() => {
    let audioCtx = null;
    let osc = null;
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (AudioCtx) {
        audioCtx = new AudioCtx();
        osc = audioCtx.createOscillator();
        const gainNode = audioCtx.createGain();
        gainNode.gain.value = 0.00001; // virtually silent, flags tab as active audio session
        osc.connect(gainNode);
        gainNode.connect(audioCtx.destination);
        osc.start();
        if (audioCtx.state === 'suspended') {
          audioCtx.resume().catch(() => {});
        }
      }
    } catch (_) {}

    return () => {
      try {
        if (osc) osc.stop();
        if (audioCtx) audioCtx.close();
      } catch (_) {}
    };
  }, []);

  // Tab switch listener: When tab is brought back to foreground, verify socket connectivity
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        if (socketRef.current) {
          if (!socketRef.current.connected) {
            console.log('Meeting tab active: reconnecting signaling socket...');
            socketRef.current.connect();
          } else {
            socketRef.current.emit('check-room-presence', { roomId, userName: currentUserName });
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', handleVisibility);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', handleVisibility);
    };
  }, [roomId, currentUserName]);

  

  // 1. Initialize Meeting Metadata & DB Record
  useEffect(() => {
    let timerInterval = null;

    const initMeeting = async () => {
      try {
        setLoading(true);
        const res = await fetchMeetingByRoomId(roomId);
        const data = res?.data || res || {};
        setMeeting(data);
        if (Array.isArray(data.messages)) setChatMessages(data.messages);
        if (Array.isArray(data.participants)) setParticipantsList(data.participants);

        timerInterval = setInterval(() => {
          setDuration((prev) => prev + 1);
        }, 1000);
      } catch (err) {
        console.error('Error initializing meeting:', err);
      } finally {
        setLoading(false);
      }
    };

    if (roomId) {
      initMeeting();
    }

    return () => {
      if (timerInterval) clearInterval(timerInterval);
      if (isLeavingMeetingRef.current) {
        leaveMeetingRoom(roomId, { participantName: currentUserName }).catch(() => {});
      }
    };
  }, [roomId, currentUserName]);

  // Helper to add local media tracks without throwing duplicate sender errors
  const addTracksToPeerConnection = useCallback((pc, stream) => {
    if (!pc || !stream) return;
    const senders = pc.getSenders();
    stream.getTracks().forEach((track) => {
      const existingSender = senders.find(
        (s) => s.track && (s.track.id === track.id || s.track.kind === track.kind)
      );
      if (existingSender) {
        if (existingSender.track !== track) {
          existingSender.replaceTrack(track).catch(console.warn);
        }
      } else {
        try {
          pc.addTrack(track, stream);
        } catch (err) {
          console.warn('Could not add track to peer connection:', err);
        }
      }
    });
  }, []);

  // Helper to create an RTCPeerConnection for a remote peer
  const createPeerConnection = useCallback((targetSocketId, targetUserName, targetEmployeeId) => {
    if (peerConnectionsRef.current[targetSocketId]) {
      return peerConnectionsRef.current[targetSocketId];
    }

    const pc = new RTCPeerConnection(ICE_SERVERS);
    peerConnectionsRef.current[targetSocketId] = pc;

    // Add local media tracks to peer connection safely
    const currentStream = screenStreamRef.current || localStreamRef.current;
    if (currentStream) {
      addTracksToPeerConnection(pc, currentStream);
    }

    // Ensure transceivers for both audio and video exist so subsequent toggles work without renegotiation
    const senders = pc.getSenders();
    if (!senders.some((s) => s.track?.kind === 'video')) {
      try {
        pc.addTransceiver('video', { direction: 'sendrecv' });
      } catch (_) {}
    }
    if (!senders.some((s) => s.track?.kind === 'audio')) {
      try {
        pc.addTransceiver('audio', { direction: 'sendrecv' });
      } catch (_) {}
    }

    // Handle ICE Candidate generated locally
    pc.onicecandidate = (event) => {
      if (event.candidate && socketRef.current) {
        socketRef.current.emit('send-ice-candidate', {
          targetSocketId,
          candidate: event.candidate
        });
      }
    };

    // Handle incoming remote media tracks (accumulate tracks into peer MediaStream)
    pc.ontrack = (event) => {
      let incomingStream = event.streams && event.streams[0];
      if (!incomingStream) {
        incomingStream = new MediaStream([event.track]);
      }
      setPeers((prev) => {
        const existing = prev[targetSocketId] || {};
        let finalStream = existing.stream;
        if (!finalStream) {
          finalStream = new MediaStream(incomingStream.getTracks());
        } else {
          if (!finalStream.getTracks().some((t) => t.id === event.track.id)) {
            finalStream.addTrack(event.track);
          }
          finalStream = new MediaStream(finalStream.getTracks());
        }

        return {
          ...prev,
          [targetSocketId]: {
            ...existing,
            socketId: targetSocketId,
            userName: targetUserName,
            employeeId: targetEmployeeId,
            stream: finalStream
          }
        };
      });

      // Track un-mutes when live frames arrive
      event.track.onunmute = () => {
        setPeers((prev) => {
          const existing = prev[targetSocketId];
          if (!existing || !existing.stream) return prev;
          return {
            ...prev,
            [targetSocketId]: {
              ...existing,
              stream: new MediaStream(existing.stream.getTracks())
            }
          };
        });
      };
    };

    pc.onconnectionstatechange = () => {
      if (pc.connectionState === 'disconnected' || pc.connectionState === 'failed' || pc.connectionState === 'closed') {
        try {
          pc.close();
        } catch (_) {}
        delete peerConnectionsRef.current[targetSocketId];
        delete candidateQueueRef.current[targetSocketId];
        setPeers((prev) => {
          const updated = { ...prev };
          delete updated[targetSocketId];
          return updated;
        });
      }
    };

    return pc;
  }, [addTracksToPeerConnection]);

  // Helper: Start Local Audio/Video Camera with audio-only fallback
  const startLocalMedia = useCallback(async () => {
    if (localStreamRef.current && localStreamRef.current.getTracks().some((t) => t.readyState === 'live')) {
      return localStreamRef.current;
    }
    const wantVideo = !isVideoOffRef.current;
    const wantAudio = true;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: wantVideo,
        audio: wantAudio
      });
      stream.getAudioTracks().forEach((track) => {
        track.enabled = !isAudioMutedRef.current;
      });
      stream.getVideoTracks().forEach((track) => {
        track.enabled = !isVideoOffRef.current;
      });
      setLocalStream(stream);
      localStreamRef.current = stream;
      setMediaError(null);
      if (socketRef.current) {
        socketRef.current.emit('media-state-change', {
          roomId,
          isVideoOff: isVideoOffRef.current,
          isAudioMuted: isAudioMutedRef.current
        });
      }
      return stream;
    } catch (err) {
      console.warn('Could not acquire requested media, falling back to audio only:', err);
      try {
        const audioStream = await navigator.mediaDevices.getUserMedia({
          video: false,
          audio: true
        });
        audioStream.getAudioTracks().forEach((track) => {
          track.enabled = !isAudioMutedRef.current;
        });
        setLocalStream(audioStream);
        localStreamRef.current = audioStream;
        setIsVideoOff(true);
        isVideoOffRef.current = true;
        setMediaError(null);
        if (socketRef.current) {
          socketRef.current.emit('media-state-change', {
            roomId,
            isVideoOff: true,
            isAudioMuted: isAudioMutedRef.current
          });
        }
        return audioStream;
      } catch (audioErr) {
        console.warn('Could not acquire microphone either:', audioErr);
        setMediaError('Camera & Microphone permissions denied.');
        return null;
      }
    }
  }, [roomId]);

  // 2. Connect to Signaling Server & Setup Admission Listeners
  useEffect(() => {
    if (loading || !roomId) return;

    let socket = null;

    const setupSignaling = async () => {
      const socketUrl = getSocketBaseUrl();
      socket = io(socketUrl, {
        auth: (callback) => callback({ token: localStorage.getItem('authToken') || '' }),
        transports: ['polling', 'websocket'],
        reconnection: true,
        reconnectionAttempts: 30,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        timeout: 20000
      });
      socketRef.current = socket;

      const emitJoin = () => {
        socket.emit('join-room', {
          roomId,
          userId: currentUserId,
          userName: currentUserName,
          employeeId: currentEmployeeId,
          isAudioMuted,
          isVideoOff
        });
      };

      socket.on('connect', () => {
        console.log('[MeetingRoom] Connected to signaling socket:', socket.id, 'at:', socketUrl);
        emitJoin();
      });

      socket.on('connect_error', (err) => {
        console.warn('[MeetingRoom] Socket connection error:', err?.message, 'at:', socketUrl);
      });

      socket.on('reconnect', () => {
        console.log('[MeetingRoom] Meeting socket reconnected, resuming room presence...');
        emitJoin();
      });

      // Passcode required
      socket.on('passcode-required', () => {
        setPasscodeRequired(true);
      });

      // User is confirmed as Host
      socket.on('host-confirmed', async () => {
        setIsAdmitted(true);
        setIsWaitingForHost(false);
        await startMeeting(roomId).catch(() => {});
        await startLocalMedia();
      });

      // User is placed in Waiting Room
      socket.on('waiting-for-admission', (data) => {
        setIsWaitingForHost(true);
        setIsAdmitted(false);
        setWaitingHostName(data?.hostName || 'The Host');
      });

      // Host admits user: Transition to live room!
      socket.on('admission-approved', async (data) => {
        setIsWaitingForHost(false);
        setIsAdmitted(true);

        const currentStream = await startLocalMedia();
        const existingUsers = data?.existingUsers || [];

        for (const user of existingUsers) {
          if (user.socketId === socket.id) continue;

          setPeers((prev) => ({
            ...prev,
            [user.socketId]: {
              socketId: user.socketId,
              userName: user.userName,
              employeeId: user.employeeId,
              isAudioMuted: user.isAudioMuted || false,
              isVideoOff: user.isVideoOff || false,
              stream: null
            }
          }));

          const pc = createPeerConnection(user.socketId, user.userName, user.employeeId);
          if (currentStream) {
            addTracksToPeerConnection(pc, currentStream);
          }

          try {
            const offer = await pc.createOffer();
            await pc.setLocalDescription(offer);
            socket.emit('send-offer', {
              targetSocketId: user.socketId,
              callerName: currentUserName,
              callerEmployeeId: currentEmployeeId,
              sdp: offer
            });
          } catch (err) {
            console.error('Error creating offer for existing user:', user.socketId, err);
          }
        }
      });

      // Host denies admission
      socket.on('admission-denied', (data) => {
        alert(data?.message || 'The host has denied your request to join this meeting.');
        navigate('/meetings');
      });

      // Host kicks participant
      socket.on('kicked-from-meeting', (data) => {
        alert(data?.message || 'You have been removed from this meeting by the host.');
        navigate('/meetings');
      });

      // Host receives a knock request from someone waiting
      socket.on('participant-knocking', (knockingUser) => {
        setAdmissionNotification(knockingUser);
      });

      // Host receives updated list of waiting users
      socket.on('waiting-users-list', (list) => {
        setWaitingUsers(Array.isArray(list) ? list : []);
      });

      // Existing admitted users (sent to host)
      socket.on('all-users', async (existingUsers) => {
        const currentStream = await startLocalMedia();

        for (const user of existingUsers) {
          if (user.socketId === socket.id) continue;

          setPeers((prev) => ({
            ...prev,
            [user.socketId]: {
              socketId: user.socketId,
              userName: user.userName,
              employeeId: user.employeeId,
              isAudioMuted: user.isAudioMuted || false,
              isVideoOff: user.isVideoOff || false,
              stream: null
            }
          }));

          const pc = createPeerConnection(user.socketId, user.userName, user.employeeId);
          if (currentStream) {
            addTracksToPeerConnection(pc, currentStream);
          }

          try {
            const offer = await pc.createOffer();
            await pc.setLocalDescription(offer);
            socket.emit('send-offer', {
              targetSocketId: user.socketId,
              callerName: currentUserName,
              callerEmployeeId: currentEmployeeId,
              sdp: offer
            });
          } catch (err) {
            console.error('Error creating offer for peer:', user.socketId, err);
          }
        }
      });

      // Another user was admitted and joined the room
      socket.on('user-joined', (newUser) => {
        setPeers((prev) => ({
          ...prev,
          [newUser.socketId]: {
            socketId: newUser.socketId,
            userName: newUser.userName,
            employeeId: newUser.employeeId,
            isAudioMuted: newUser.isAudioMuted || false,
            isVideoOff: newUser.isVideoOff || false,
            stream: null
          }
        }));

        setParticipantsList((prev) => {
          const exists = prev.some((p) => p.participantName === newUser.userName);
          if (exists) return prev;
          return [
            ...prev,
            {
              id: newUser.socketId,
              participantName: newUser.userName,
              employeeId: newUser.employeeId,
              role: 'PARTICIPANT',
              joinedAt: new Date().toISOString()
            }
          ];
        });
      });

      // Receive WebRTC Offer
      socket.on('receive-offer', async (data) => {
        const { callerSocketId, callerName, callerEmployeeId, sdp } = data;
        const pc = createPeerConnection(callerSocketId, callerName, callerEmployeeId);

        const currentStream = screenStreamRef.current || localStreamRef.current;
        if (currentStream) {
          addTracksToPeerConnection(pc, currentStream);
        }

        try {
          await pc.setRemoteDescription(new RTCSessionDescription(sdp));

          // Drain queued ICE candidates
          const queued = candidateQueueRef.current[callerSocketId];
          if (queued && queued.length > 0) {
            for (const cand of queued) {
              await pc.addIceCandidate(new RTCIceCandidate(cand)).catch(console.warn);
            }
            delete candidateQueueRef.current[callerSocketId];
          }

          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);

          socket.emit('send-answer', {
            targetSocketId: callerSocketId,
            sdp: answer
          });

          // Check if video track now exists on peer's receiver and bind to stream
          const transceivers = pc.getTransceivers ? pc.getTransceivers() : [];
          const videoReceiver = transceivers.find(
            (t) => t.receiver && t.receiver.track && t.receiver.track.kind === 'video'
          );
          if (videoReceiver?.receiver?.track) {
            const vTrack = videoReceiver.receiver.track;
            setPeers((prev) => {
              const existing = prev[callerSocketId] || {};
              let s = existing.stream;
              if (!s) {
                s = new MediaStream([vTrack]);
              } else if (!s.getVideoTracks().some((t) => t.id === vTrack.id)) {
                s.addTrack(vTrack);
                s = new MediaStream(s.getTracks());
              }
              return {
                ...prev,
                [callerSocketId]: {
                  ...existing,
                  stream: s
                }
              };
            });
          }
        } catch (err) {
          console.error('Error handling receive-offer from peer:', callerSocketId, err);
        }
      });

      // Receive WebRTC Answer
      socket.on('receive-answer', async (data) => {
        const { senderSocketId, sdp } = data;
        const pc = peerConnectionsRef.current[senderSocketId];
        if (pc) {
          try {
            await pc.setRemoteDescription(new RTCSessionDescription(sdp));

            // Drain queued ICE candidates
            const queued = candidateQueueRef.current[senderSocketId];
            if (queued && queued.length > 0) {
              for (const cand of queued) {
                await pc.addIceCandidate(new RTCIceCandidate(cand)).catch(console.warn);
              }
              delete candidateQueueRef.current[senderSocketId];
            }
          } catch (err) {
            console.error('Error setting remote description from answer:', err);
          }
        }
      });

      // Receive ICE Candidate
      socket.on('receive-ice-candidate', async (data) => {
        const { senderSocketId, candidate } = data;
        if (!candidate) return;
        const pc = peerConnectionsRef.current[senderSocketId];
        if (pc && pc.remoteDescription && pc.remoteDescription.type) {
          try {
            await pc.addIceCandidate(new RTCIceCandidate(candidate));
          } catch (err) {
            console.warn('Error adding ICE candidate:', err);
          }
        } else {
          if (!candidateQueueRef.current[senderSocketId]) {
            candidateQueueRef.current[senderSocketId] = [];
          }
          candidateQueueRef.current[senderSocketId].push(candidate);
        }
      });

      // Remote peer media state change
      socket.on('peer-media-state', (data) => {
        setPeers((prev) => {
          if (!prev[data.socketId]) return prev;
          const peer = prev[data.socketId];
          let updatedStream = peer.stream;

          // When peer starts screen sharing or turns on video, bind video transceiver track
          if (data.isScreenSharing || data.isVideoOff === false) {
            const pc = peerConnectionsRef.current[data.socketId];
            if (pc) {
              const transceivers = pc.getTransceivers ? pc.getTransceivers() : [];
              const videoReceiver = transceivers.find(
                (t) => t.receiver && t.receiver.track && t.receiver.track.kind === 'video'
              );
              const vTrack = videoReceiver?.receiver?.track;
              if (vTrack) {
                if (!updatedStream) {
                  updatedStream = new MediaStream([vTrack]);
                } else if (!updatedStream.getVideoTracks().some((t) => t.id === vTrack.id)) {
                  updatedStream.addTrack(vTrack);
                  updatedStream = new MediaStream(updatedStream.getTracks());
                }
              }
            }
          }

          return {
            ...prev,
            [data.socketId]: {
              ...peer,
              stream: updatedStream,
              ...(data.isAudioMuted !== undefined ? { isAudioMuted: data.isAudioMuted } : {}),
              ...(data.isVideoOff !== undefined ? { isVideoOff: data.isVideoOff } : {}),
              ...(data.isScreenSharing !== undefined ? { isScreenSharing: data.isScreenSharing } : {})
            }
          };
        });
      });

      // In-Call Live Chat Message received
      socket.on('new-chat-message', (newMsg) => {
        setChatMessages((prev) => [...prev, newMsg]);
        setActiveDrawer((currentDrawer) => {
          if (currentDrawer !== 'chat') {
            setUnreadChatCount((cnt) => cnt + 1);
          }
          return currentDrawer;
        });
      });

      // User Left Room
      socket.on('user-left', (data) => {
        const pc = peerConnectionsRef.current[data.socketId];
        if (pc) {
          try {
            pc.close();
          } catch (_) {}
          delete peerConnectionsRef.current[data.socketId];
        }

        setPeers((prev) => {
          const updated = { ...prev };
          delete updated[data.socketId];
          return updated;
        });

        setParticipantsList((prev) =>
          prev.map((p) =>
            p.participantName === data.userName ? { ...p, leftAt: new Date().toISOString() } : p
          )
        );
      });

      // Call declined by invited user
      socket.on('meeting-call-declined', (data) => {
        const declinedEmpId = data?.targetEmployeeId || data?.employeeId;
        const declinedUserId = data?.targetUserId;
        const declinedEmpCode = data?.targetEmployeeCode;
        const declinedName = data?.targetName || data?.userName || 'User';

        const keysToSet = [];
        if (declinedEmpId) keysToSet.push(`emp_${declinedEmpId}`);
        if (declinedUserId) keysToSet.push(`user_${declinedUserId}`);
        if (declinedEmpCode) keysToSet.push(`code_${declinedEmpCode}`);

        setCallingUsersMap((prev) => {
          const next = { ...prev };
          keysToSet.forEach((k) => {
            next[k] = 'declined';
          });
          return next;
        });

        setDeclinedToast({
          name: declinedName,
          timestamp: Date.now()
        });

        setTimeout(() => {
          setDeclinedToast(null);
          setCallingUsersMap((prev) => {
            const next = { ...prev };
            keysToSet.forEach((k) => {
              if (next[k] === 'declined') delete next[k];
            });
            return next;
          });
        }, 4000);
      });

      socket.on('meeting-ended', () => {
        if (isLeavingMeetingRef.current) return;
        isLeavingMeetingRef.current = true;
        handleClose();
        navigate('/meetings');
      });
    };

    setupSignaling();

    // Cleanup when leaving room or unmounting
    return () => {
      // ONLY stop media tracks & disconnect socket if user is ACTUALLY leaving/ending the meeting
      if (isLeavingMeetingRef.current) {
        if (localStreamRef.current) {
          localStreamRef.current.getTracks().forEach((track) => track.stop());
          localStreamRef.current = null;
        }
        if (screenStreamRef.current) {
          screenStreamRef.current.getTracks().forEach((track) => track.stop());
          screenStreamRef.current = null;
        }

        Object.values(peerConnectionsRef.current).forEach((pc) => {
          try {
            pc.close();
          } catch (_) {}
        });
        peerConnectionsRef.current = {};

        if (socketRef.current) {
          socketRef.current.emit('leave-room', {
            roomId,
            userName: currentUserName
          });
          socketRef.current.disconnect();
        }
      }
    };
  }, [loading, roomId, currentUserName, currentUserId, currentEmployeeId, navigate]);

  // Scroll chat to bottom
  useEffect(() => {
    if (activeDrawer === 'chat' && chatBottomRef.current) {
      chatBottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages, activeDrawer]);

  // Host Admits User
  const handleAdmitUser = (targetSocketId) => {
    if (socketRef.current) {
      socketRef.current.emit('admit-user', { roomId, targetSocketId });
    }
    if (admissionNotification?.socketId === targetSocketId) {
      setAdmissionNotification(null);
    }
  };

  // Host Denies User
  const handleDenyUser = (targetSocketId) => {
    if (socketRef.current) {
      socketRef.current.emit('deny-user', { roomId, targetSocketId });
    }
    if (admissionNotification?.socketId === targetSocketId) {
      setAdmissionNotification(null);
    }
  };

  // Host Kicks Participant
  const handleKickParticipant = (targetSocketId, name) => {
    if (window.confirm(`Are you sure you want to remove ${name} from this meeting?`)) {
      if (socketRef.current) {
        socketRef.current.emit('kick-user', { roomId, targetSocketId });
      }
    }
  };

  // Submit Passcode
  const handlePasscodeSubmit = (e) => {
    e.preventDefault();
    if (!passcodeInput.trim()) return;
    setPasscodeRequired(false);
    if (socketRef.current) {
      socketRef.current.emit('join-room', {
        roomId,
        userId: currentUserId,
        userName: currentUserName,
        employeeId: currentEmployeeId,
        passcode: passcodeInput.trim(),
        isAudioMuted,
        isVideoOff
      });
    }
  };

  // Toggle Microphone
  const toggleAudio = async () => {
    try {
      let currentStream = localStreamRef.current;
      let audioTrack = currentStream ? currentStream.getAudioTracks()[0] : null;

      // If no audio track exists, attempt to acquire microphone
      if (!audioTrack || audioTrack.readyState === 'ended') {
        const newMedia = await navigator.mediaDevices.getUserMedia({
          audio: true
        });
        const newTrack = newMedia.getAudioTracks()[0];
        if (!newTrack) return;

        if (!currentStream) {
          currentStream = new MediaStream([newTrack]);
        } else {
          currentStream.addTrack(newTrack);
        }
        localStreamRef.current = currentStream;
        setLocalStream(new MediaStream(currentStream.getTracks()));
        setIsAudioMuted(false);

        // Update all peer connections
        Object.values(peerConnectionsRef.current).forEach((pc) => {
          const senders = pc.getSenders();
          const audioSender = senders.find((s) => s.track?.kind === 'audio' || (!s.track && s.dtlsTransport));
          if (audioSender) {
            audioSender.replaceTrack(newTrack).catch(console.warn);
          } else {
            try {
              pc.addTrack(newTrack, currentStream);
            } catch (err) {
              console.warn('Could not add audio track to pc:', err);
            }
          }
        });

        if (socketRef.current) {
          socketRef.current.emit('media-state-change', {
            roomId,
            isAudioMuted: false
          });
        }
        return;
      }

      const nextMuted = !isAudioMuted;
      setIsAudioMuted(nextMuted);
      isAudioMutedRef.current = nextMuted;
      localStorage.setItem(mediaPreferenceKey, JSON.stringify({ isAudioMuted: nextMuted, isVideoOff: isVideoOffRef.current }));

      if (audioTrack) {
        audioTrack.enabled = !nextMuted;
      }

      if (socketRef.current) {
        socketRef.current.emit('media-state-change', {
          roomId,
          isAudioMuted: nextMuted
        });
      }
    } catch (err) {
      console.warn('Could not toggle microphone:', err);
    }
  };

  // Toggle Camera
  const toggleVideo = async () => {
    try {
      let currentStream = localStreamRef.current;
      let videoTrack = currentStream ? currentStream.getVideoTracks()[0] : null;

      // If no video track exists (e.g. joined in audio-only fallback mode), acquire camera now
      if (!videoTrack || videoTrack.readyState === 'ended') {
        const newMedia = await navigator.mediaDevices.getUserMedia({
          video: true
        });
        const newTrack = newMedia.getVideoTracks()[0];
        if (!newTrack) return;

        if (!currentStream) {
          currentStream = new MediaStream([newTrack]);
        } else {
          currentStream.addTrack(newTrack);
        }
        localStreamRef.current = currentStream;
        setLocalStream(new MediaStream(currentStream.getTracks()));
        setIsVideoOff(false);

        // Update video sender across all active peer connections
        Object.values(peerConnectionsRef.current).forEach((pc) => {
          const senders = pc.getSenders();
          const videoSender = senders.find((s) => s.track?.kind === 'video' || (!s.track && s.dtlsTransport));
          if (videoSender) {
            videoSender.replaceTrack(newTrack).catch(console.warn);
          } else {
            try {
              pc.addTrack(newTrack, currentStream);
            } catch (err) {
              console.warn('Could not add new track to pc:', err);
            }
          }
        });

        if (socketRef.current) {
          socketRef.current.emit('media-state-change', {
            roomId,
            isVideoOff: false
          });
        }
        return;
      }

      const nextOff = !isVideoOff;
      setIsVideoOff(nextOff);
      isVideoOffRef.current = nextOff;
      localStorage.setItem(mediaPreferenceKey, JSON.stringify({ isAudioMuted: isAudioMutedRef.current, isVideoOff: nextOff }));

      if (videoTrack) {
        videoTrack.enabled = !nextOff;
      }

      // Verify that senders are sending this track
      Object.values(peerConnectionsRef.current).forEach((pc) => {
        const senders = pc.getSenders();
        const videoSender = senders.find((s) => s.track?.kind === 'video' || (!s.track && s.dtlsTransport));
        if (videoSender && videoSender.track !== videoTrack) {
          videoSender.replaceTrack(videoTrack).catch(console.warn);
        }
      });

      if (socketRef.current) {
        socketRef.current.emit('media-state-change', {
          roomId,
          isVideoOff: nextOff
        });
      }
    } catch (err) {
      console.warn('Could not toggle camera:', err);
      alert('Could not start camera. If you are testing with two tabs on the same computer, the other tab might be locking the webcam.');
    }
  };

  // Toggle Screen Sharing
  const toggleScreenShare = async () => {
    if (!isScreenSharing) {
      try {
        const displayStream = await navigator.mediaDevices.getDisplayMedia({
          video: true,
          audio: false
        });
        setScreenStream(displayStream);
        screenStreamRef.current = displayStream;
        setIsScreenSharing(true);

        const screenTrack = displayStream.getVideoTracks()[0];

        // Attach screen track to all active peer connections
        for (const [targetSocketId, pc] of Object.entries(peerConnectionsRef.current)) {
          const transceivers = pc.getTransceivers ? pc.getTransceivers() : [];
          const videoTransceiver = transceivers.find(
            (t) => (t.sender && t.sender.track && t.sender.track.kind === 'video') ||
                   (t.receiver && t.receiver.track && t.receiver.track.kind === 'video') ||
                   (t.mid && String(t.mid).toLowerCase().includes('video'))
          );

          if (videoTransceiver && videoTransceiver.sender) {
            videoTransceiver.direction = 'sendrecv';
            await videoTransceiver.sender.replaceTrack(screenTrack).catch(console.warn);
          } else {
            const sender = pc.getSenders().find((s) => s.track?.kind === 'video');
            if (sender) {
              await sender.replaceTrack(screenTrack).catch(console.warn);
            } else {
              try {
                pc.addTrack(screenTrack, displayStream);
              } catch (err) {
                console.warn('Could not add screen track to pc:', err);
              }
            }
          }

          // Trigger renegotiation offer so remote peer receives and binds video track
          try {
            const offer = await pc.createOffer();
            await pc.setLocalDescription(offer);
            if (socketRef.current) {
              socketRef.current.emit('send-offer', {
                targetSocketId,
                callerName: currentUserName,
                callerEmployeeId: currentEmployeeId,
                sdp: offer
              });
            }
          } catch (renegErr) {
            console.warn('Renegotiation offer error during screen share:', renegErr);
          }
        }

        if (socketRef.current) {
          socketRef.current.emit('media-state-change', {
            roomId,
            isScreenSharing: true
          });
        }

        screenTrack.onended = () => {
          stopScreenSharing();
        };
      } catch (err) {
        console.warn('Screen share cancelled or failed:', err);
      }
    } else {
      stopScreenSharing();
    }
  };

  const stopScreenSharing = () => {
    if (screenStreamRef.current) {
      screenStreamRef.current.getTracks().forEach((track) => track.stop());
      screenStreamRef.current = null;
    }
    setScreenStream(null);
    setIsScreenSharing(false);

    const cameraVideoTrack = localStreamRef.current?.getVideoTracks()[0];
    Object.entries(peerConnectionsRef.current).forEach(async ([targetSocketId, pc]) => {
      const transceivers = pc.getTransceivers ? pc.getTransceivers() : [];
      const videoTransceiver = transceivers.find(
        (t) => (t.sender && t.sender.track && t.sender.track.kind === 'video') ||
               (t.receiver && t.receiver.track && t.receiver.track.kind === 'video')
      );
      if (videoTransceiver && videoTransceiver.sender) {
        if (cameraVideoTrack && !isVideoOffRef.current) {
          videoTransceiver.direction = 'sendrecv';
          await videoTransceiver.sender.replaceTrack(cameraVideoTrack).catch(console.warn);
        } else {
          videoTransceiver.direction = 'recvonly';
          await videoTransceiver.sender.replaceTrack(null).catch(console.warn);
        }
      }

      try {
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        if (socketRef.current) {
          socketRef.current.emit('send-offer', {
            targetSocketId,
            callerName: currentUserName,
            callerEmployeeId: currentEmployeeId,
            sdp: offer
          });
        }
      } catch (_) {}
    });

    if (socketRef.current) {
      socketRef.current.emit('media-state-change', {
        roomId,
        isScreenSharing: false
      });
    }
  };

  // Send In-Meeting Chat
  const handleSendMessage = (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || sendingChat) return;

    const messageText = inputMessage.trim();
    setInputMessage('');
    setSendingChat(true);

    if (socketRef.current) {
      socketRef.current.emit('send-chat', {
        roomId,
        message: messageText,
        senderName: currentUserName,
        senderEmployeeId: currentEmployeeId
      });
    }

    setSendingChat(false);
  };

  // Pre-load active company employees so they are instantly searchable for MS Teams call pull
  useEffect(() => {
    fetchMeetingInvitees()
      .then((res) => {
        const list = res?.data || res || [];
        if (Array.isArray(list)) setAvailableInvitees(list);
      })
      .catch(() => {});
  }, []);

  // MS Teams Call Pull: Invite / pull company employee directly into this live meeting
  const handlePullUserToCall = (emp) => {
    if (!emp || !socketRef.current) return;
    const empKey = `emp_${emp.id || emp.employeeId}`;
    setCallingUsersMap((prev) => ({ ...prev, [empKey]: true }));

    socketRef.current.emit('pull-user-to-call', {
      roomId,
      meetingTitle: meeting?.title || 'Virtual Meeting',
      callerName: currentUserName || 'Host',
      targetUserId: emp.userId ? Number(emp.userId) : null,
      targetEmployeeId: emp.employeeId || (emp.id ? String(emp.id) : null),
      targetEmployeeCode: emp.employeeCode || null,
      targetName: emp.name,
      targetEmail: emp.email
    });

    // Re-enable button after 30s
    setTimeout(() => {
      setCallingUsersMap((prev) => {
        const next = { ...prev };
        delete next[empKey];
        return next;
      });
    }, 30000);
  };

  // Filter invitees search
  const filteredInvitees = useMemo(() => {
    const query = pullSearch.trim().toLowerCase();
    const activeNames = new Set([
      (currentUserName || '').toLowerCase(),
      ...remotePeerList.map((p) => (p.userName || '').toLowerCase())
    ]);

    return availableInvitees.filter((emp) => {
      if (activeNames.has((emp.name || '').toLowerCase())) return false;
      if (!query) return true;
      return (
        (emp.name && emp.name.toLowerCase().includes(query)) ||
        (emp.email && emp.email.toLowerCase().includes(query)) ||
        (emp.employeeCode && emp.employeeCode.toLowerCase().includes(query))
      );
    });
  }, [availableInvitees, pullSearch, currentUserName, remotePeerList]);

  const isHost = Boolean(
    (meeting?.hostUserId && currentUserId && String(meeting.hostUserId) === String(currentUserId)) ||
    (meeting?.hostEmployeeId && currentEmployeeId && String(meeting.hostEmployeeId) === String(currentEmployeeId)) ||
    (meeting?.hostName && currentUserName && meeting.hostName.trim().toLowerCase() === currentUserName.trim().toLowerCase())
  );

  // Leave Meeting (Status automatically marks as COMPLETED when all participants have left)
  const handleLeaveMeeting = () => {
    if (isLeavingMeetingRef.current) return;
    isLeavingMeetingRef.current = true;

    // 1. Immediately stop local media tracks so mic & camera shut off instantly
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((track) => track.stop());
      localStreamRef.current = null;
    }
    if (screenStreamRef.current) {
      screenStreamRef.current.getTracks().forEach((track) => track.stop());
      screenStreamRef.current = null;
    }

    // 2. Immediately close all WebRTC peer connections
    Object.values(peerConnectionsRef.current).forEach((pc) => {
      try {
        pc.close();
      } catch (_) {}
    });
    peerConnectionsRef.current = {};

    // 3. Notify signaling server and disconnect socket immediately
    if (socketRef.current) {
      try {
        socketRef.current.emit('leave-room', {
          roomId,
          userName: currentUserName
        });
        socketRef.current.disconnect();
      } catch (_) {}
    }

    // 4. Immediately close meeting in context and clean sessionStorage
    handleClose();

    // 5. Immediately navigate to meetings list
    navigate('/meetings');

    // 6. Record participant leave in database asynchronously & auto-complete if everyone left
    leaveMeetingRoom(roomId, { participantName: currentUserName }).catch(() => {});
  };

  const handleCopyLink = () => {
    const link = `${window.location.origin}/meetings/room/${roomId}`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const toggleDrawer = (drawerType) => {
    if (activeDrawer === drawerType) {
      setActiveDrawer(null);
    } else {
      setActiveDrawer(drawerType);
      if (drawerType === 'chat') {
        setUnreadChatCount(0);
      }
    }
  };

  const formatDuration = (totalSeconds) => {
    const hrs = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;
    const pad = (n) => String(n).padStart(2, '0');
    return hrs > 0 ? `${pad(hrs)}:${pad(mins)}:${pad(secs)}` : `${pad(mins)}:${pad(secs)}`;
  };

  const totalInCall = remotePeerList.length + 1;

  const remoteScreenSharer = remotePeerList.find((p) => p.isScreenSharing);
  const activeScreenStream = useMemo(() => {
    if (isScreenSharing) return screenStream;
    if (!remoteScreenSharer) return null;

    let s = remoteScreenSharer.stream;
    const hasVideo = s && s.getVideoTracks && s.getVideoTracks().length > 0;
    if (!hasVideo) {
      const pc = peerConnectionsRef.current[remoteScreenSharer.socketId];
      if (pc) {
        const transceivers = pc.getTransceivers ? pc.getTransceivers() : [];
        const videoReceiver = transceivers.find(
          (t) => (t.receiver && t.receiver.track && t.receiver.track.kind === 'video') ||
                 (t.mid && String(t.mid).toLowerCase().includes('video'))
        );
        const vTrack = videoReceiver?.receiver?.track;
        if (vTrack) {
          s = new MediaStream([vTrack, ...(s ? s.getAudioTracks() : [])]);
        }
      }
    }
    return s;
  }, [isScreenSharing, screenStream, remoteScreenSharer, peers]);

  // ---------------------------------------------------------------------------
  // Case A: Waiting Room Screen (Participant is waiting for host to admit)
  // ---------------------------------------------------------------------------
  if (isWaitingForHost) {
    return (
      <div className="google-meet-room">
        <header className="meet-topbar">
          <div className="meet-topbar-left">
            <button className="meet-back-btn" onClick={handleLeaveMeeting} title="Leave">
              <ArrowLeftOutlined />
            </button>
            <div className="meet-info-group">
              <h1 className="meet-title">{meeting?.title || 'Virtual Meeting'}</h1>
              <div className="meet-badge-cluster">
                <span className="meet-room-code">{roomId}</span>
              </div>
            </div>
          </div>
          <div className="meet-topbar-right">
            <button className="meet-leave-btn" onClick={handleLeaveMeeting}>
              <PhoneOutlined className="meet-hangup-icon" /> Cancel & Leave
            </button>
          </div>
        </header>

        <div className="meet-waiting-room-stage">
          <div className="meet-waiting-card">
            <div className="meet-waiting-pulse-ring">
              <LoadingOutlined spin className="meet-waiting-spinner" />
            </div>
            <h2 className="meet-waiting-title">Waiting for host to admit you...</h2>
            <p className="meet-waiting-desc">
              The host (<strong>{waitingHostName}</strong>) has been notified that you are waiting.
              You will automatically join as soon as they admit you.
            </p>
            <div className="meet-waiting-meta">
              <span>Your Name: <strong>{currentUserName}</strong></span>
              {currentEmployeeId && <span>Employee ID: <strong>{currentEmployeeId}</strong></span>}
            </div>
            <button className="meet-waiting-cancel-btn" onClick={handleLeaveMeeting}>
              Cancel Request
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Case B: Passcode Required Prompt
  // ---------------------------------------------------------------------------
  if (passcodeRequired) {
    return (
      <div className="google-meet-room">
        <header className="meet-topbar">
          <div className="meet-topbar-left">
            <button className="meet-back-btn" onClick={handleLeaveMeeting}>
              <ArrowLeftOutlined />
            </button>
            <h1 className="meet-title">{meeting?.title || 'Virtual Meeting'}</h1>
          </div>
        </header>
        <div className="meet-waiting-room-stage">
          <form className="meet-passcode-card" onSubmit={handlePasscodeSubmit}>
            <SafetyCertificateOutlined className="meet-passcode-icon" />
            <h2>Passcode Required</h2>
            <p>This meeting is private. Please enter the passcode to enter.</p>
            <input
              type="password"
              placeholder="Enter meeting passcode..."
              value={passcodeInput}
              onChange={(e) => setPasscodeInput(e.target.value)}
              className="meet-passcode-input"
              autoFocus
            />
            <div className="meet-passcode-actions">
              <button type="button" className="meet-passcode-cancel" onClick={handleLeaveMeeting}>
                Cancel
              </button>
              <button type="submit" className="meet-passcode-submit" disabled={!passcodeInput.trim()}>
                Enter Meeting
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Case C-1: Minimized Picture-in-Picture (PiP) Floating Overlay
  // ---------------------------------------------------------------------------
  if (isMinimized) {
    const remotePeerList = Object.values(peers);
    const activePipPeer = remotePeerList.find((p) => !p.isVideoOff && p.stream) || remotePeerList[0];
    const activePipStream = activePipPeer?.stream || localStream;
    const activePipName = activePipPeer?.userName || `${currentUserName} (You)`;
    const isLocalPip = !activePipPeer;
    const isPipAudioMuted = isLocalPip ? isAudioMuted : activePipPeer?.isAudioMuted;
    const isPipVideoOff = isLocalPip ? isVideoOff : activePipPeer?.isVideoOff;

    // Ultra-Compact Background Audio Pill Mode (when collapsed) - Moveable / Draggable
    if (isPipCollapsed) {
      return (
        <div
          ref={pillRef}
          className={`google-meet-room is-pip-mode is-collapsed-pill ${isDraggingPill ? 'is-dragging' : ''}`}
          style={
            pillPos.x !== null && pillPos.y !== null
              ? { left: `${pillPos.x}px`, top: `${pillPos.y}px`, right: 'auto', bottom: 'auto' }
              : undefined
          }
          onMouseDown={handlePillMouseDown}
          onTouchStart={handlePillTouchStart}
        >
          {/* Continuous background audio stream playback for all remote participants */}
          {remotePeerList.map((peer) => (
            <audio
              key={peer.socketId}
              autoPlay
              playsInline
              ref={(el) => {
                if (el && peer.stream && el.srcObject !== peer.stream) {
                  el.srcObject = peer.stream;
                  el.play().catch(() => {});
                }
              }}
              className="meet-hidden-audio"
            />
          ))}

          <div className="meet-pill-left" onClick={handlePillClick} title="Drag to move, click to Maximize">
            <HolderOutlined className="meet-pill-drag-grip" />
            <span className="meet-live-pulse" />
            <span className="meet-pill-title" title={meeting?.title || 'Virtual Meeting'}>
              {meeting?.title || 'Virtual Meeting'}
            </span>
            <span className="meet-pip-timer">{formatDuration(duration)}</span>
          </div>

          <div className="meet-pill-controls">
            <button
              className={`meet-pip-ctrl-btn ${isAudioMuted ? 'off' : ''}`}
              onClick={toggleAudio}
              title={isAudioMuted ? 'Unmute Mic' : 'Mute Mic'}
            >
              {isAudioMuted ? <AudioMutedOutlined /> : <AudioOutlined />}
            </button>
            <button
              className={`meet-pip-ctrl-btn ${isVideoOff ? 'off' : ''}`}
              onClick={toggleVideo}
              title={isVideoOff ? 'Turn Camera On' : 'Turn Camera Off'}
            >
              <VideoCameraOutlined />
            </button>
            <button
              className="meet-pip-icon-btn"
              onClick={() => setIsPipCollapsed(false)}
              title="Expand to Floating Video Window"
            >
              <UpOutlined />
            </button>
            <button
              className="meet-pip-icon-btn"
              onClick={handleMaximize}
              title="Maximize to Full Screen"
            >
              <ExpandOutlined />
            </button>
            <button
              className="meet-pip-icon-btn leave-btn"
              onClick={handleLeaveMeeting}
              title="Leave Call"
            >
              <PhoneOutlined className="meet-hangup-icon" />
            </button>
          </div>
        </div>
      );
    }

    // Floating Video Card Mode
    return (
      <div className="google-meet-room is-pip-mode">
        {/* Continuous background audio stream playback for other remote participants */}
        {remotePeerList
          .filter((p) => p.socketId !== activePipPeer?.socketId)
          .map((peer) => (
            <audio
              key={peer.socketId}
              autoPlay
              playsInline
              ref={(el) => {
                if (el && peer.stream && el.srcObject !== peer.stream) {
                  el.srcObject = peer.stream;
                  el.play().catch(() => {});
                }
              }}
              className="meet-hidden-audio"
            />
          ))}

        <div className="meet-pip-header">
          <div className="meet-pip-header-left">
            <span className="meet-live-pulse" />
            <span className="meet-pip-title" title={meeting?.title || 'Virtual Meeting'}>
              {meeting?.title || 'Virtual Meeting'}
            </span>
            <span className="meet-pip-timer">{formatDuration(duration)}</span>
          </div>
          <div className="meet-pip-header-right">
            <button
              className="meet-pip-icon-btn"
              onClick={() => setIsPipCollapsed(true)}
              title="Collapse to Minimal Audio Bar (Background Mode)"
            >
              <MinusOutlined />
            </button>
            <button className="meet-pip-icon-btn" onClick={handleMaximize} title="Maximize to Full Screen">
              <ExpandOutlined />
            </button>
            <button className="meet-pip-icon-btn leave-btn" onClick={handleLeaveMeeting} title="Leave Call">
              <PhoneOutlined className="meet-hangup-icon" />
            </button>
          </div>
        </div>

        <div className="meet-pip-video-stage" onClick={handleMaximize} title="Click to Maximize Call">
          <VideoTile
            stream={activePipStream}
            userName={activePipName}
            isLocal={isLocalPip}
            isAudioMuted={isPipAudioMuted}
            isVideoOff={isPipVideoOff}
          />
          <div className="meet-pip-hover-overlay">
            <ExpandOutlined className="meet-pip-expand-icon" />
            <span>Click to Maximize</span>
          </div>
        </div>

        <div className="meet-pip-footer">
          <div className="meet-pip-footer-left">
            <button
              className={`meet-pip-ctrl-btn ${isAudioMuted ? 'off' : ''}`}
              onClick={toggleAudio}
              title={isAudioMuted ? 'Unmute Mic' : 'Mute Mic'}
            >
              {isAudioMuted ? <AudioMutedOutlined /> : <AudioOutlined />}
            </button>
            <button
              className={`meet-pip-ctrl-btn ${isVideoOff ? 'off' : ''}`}
              onClick={toggleVideo}
              title={isVideoOff ? 'Turn Camera On' : 'Turn Camera Off'}
            >
              <VideoCameraOutlined />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Case C-2: Main Full-Screen Video Meeting Room
  // ---------------------------------------------------------------------------
  return (
    <div className="google-meet-room is-fullscreen-view">
      {/* Top Header Bar */}
      <header className="meet-topbar">
        <div className="meet-topbar-left">
          <button className="meet-back-btn" onClick={handleMinimize} title="Minimize to browse HRMS">
            <ArrowLeftOutlined />
          </button>
          <div className="meet-info-group">
            <h1 className="meet-title">{meeting?.title || 'Virtual Meeting'}</h1>
            <div className="meet-badge-cluster">
              <span className="meet-live-badge">
                <span className="meet-live-pulse" /> LIVE
              </span>
              <span className="meet-timer">{formatDuration(duration)}</span>
              <span className="meet-room-code" onClick={handleCopyLink} title="Click to copy room link">
                {roomId} {copied ? <CheckCircleOutlined className="meet-copied-icon" /> : <CopyOutlined />}
              </span>
            </div>
          </div>
        </div>

        <div className="meet-topbar-right">
          {/* Waiting Room Badge for Host */}
          {isHost && waitingUsers.length > 0 && (
            <button
              className="meet-waiting-badge-btn"
              onClick={() => toggleDrawer('participants')}
              title={`${waitingUsers.length} participant(s) waiting in lobby`}
            >
              Waiting: {waitingUsers.length}
            </button>
          )}

          <button
            className={`meet-action-btn ${activeDrawer === 'participants' ? 'active' : ''}`}
            onClick={() => toggleDrawer('participants')}
            title="Meeting Attendees"
          >
            <TeamOutlined />
            <span className="meet-action-btn-text">Attendees ({totalInCall})</span>
          </button>

          <button
            className={`meet-action-btn ${activeDrawer === 'chat' ? 'active' : ''}`}
            onClick={() => toggleDrawer('chat')}
            title="In-Meeting Chat"
          >
            <MessageOutlined />
            <span className="meet-action-btn-text">In-Call Chat</span>
            {unreadChatCount > 0 && <span className="meet-badge-count">{unreadChatCount}</span>}
          </button>



          

          <button className="meet-leave-btn" onClick={handleLeaveMeeting}>
            <PhoneOutlined className="meet-hangup-icon" /> Leave
          </button>
        </div>
      </header>

      {/* Main Conference Stage */}
      <div className="meet-stage-wrapper">
        <div className="meet-video-container">
          {/* Host Knocking Notification Popup */}
          {isHost && admissionNotification && (
            <div className="meet-knock-toast">
              <div className="meet-knock-info">
                <strong>{admissionNotification.userName}</strong>
                <span>is waiting to join this meeting</span>
              </div>
              <div className="meet-knock-actions">
                <button
                  className="meet-knock-deny-btn"
                  onClick={() => handleDenyUser(admissionNotification.socketId)}
                >
                  Deny
                </button>
                <button
                  className="meet-knock-admit-btn"
                  onClick={() => handleAdmitUser(admissionNotification.socketId)}
                >
                  Admit
                </button>
              </div>
            </div>
          )}

          {/* Call Declined Notification Popup */}
          {declinedToast && (
            <div
              className="meet-call-declined-toast"
              onClick={() => setDeclinedToast(null)}
              style={{ cursor: 'pointer' }}
              title="Click to dismiss"
            >
              <CloseOutlined style={{ color: '#ef4444', fontSize: '15px' }} />
              <span><strong>{declinedToast.name}</strong> declined the call</span>
            </div>
          )}

          {loading ? (
            <div className="meet-loading-overlay">
              <LoadingOutlined spin className="meet-loading-spinner" />
              <p>Entering secure meeting room...</p>
            </div>
          ) : mediaError ? (
            <div className="meet-media-warning">
              <SafetyCertificateOutlined className="meet-media-warning-icon" />
              <div>
                <strong>Notice: {mediaError}</strong>
                <p>You can still participate in chat, listen to colleagues, and share your screen.</p>
              </div>
            </div>
          ) : null}

          {/* Video Grid Layout */}
          {activeScreenStream ? (
            <div className="meet-spotlight-layout">
              <div className="meet-spotlight-main">
                <VideoTile
                  stream={activeScreenStream}
                  userName={isScreenSharing ? `${currentUserName}'s Screen` : `${remoteScreenSharer?.userName}'s Screen`}
                  isLocal={isScreenSharing}
                  isScreenShare={true}
                />
              </div>
              <div className="meet-spotlight-sidebar">
                <VideoTile
                  stream={localStream}
                  userName={currentUserName}
                  isLocal={true}
                  isAudioMuted={isAudioMuted}
                  isVideoOff={isVideoOff}
                />
                {remotePeerList.map((peer) => (
                  <VideoTile
                    key={peer.socketId}
                    stream={peer.stream}
                    userName={peer.userName}
                    isAudioMuted={peer.isAudioMuted}
                    isVideoOff={peer.isVideoOff}
                  />
                ))}
              </div>
            </div>
          ) : (
            <div className={`meet-tiles-grid count-${Math.min(totalInCall, 6)}`}>
              {/* Local User Tile */}
              <VideoTile
                stream={localStream}
                userName={currentUserName}
                isLocal={true}
                isAudioMuted={isAudioMuted}
                isVideoOff={isVideoOff}
              />

              {/* Remote Participants Tiles */}
              {remotePeerList.map((peer) => (
                <VideoTile
                  key={peer.socketId}
                  stream={peer.stream}
                  userName={peer.userName}
                  isAudioMuted={peer.isAudioMuted}
                  isVideoOff={peer.isVideoOff}
                />
              ))}
            </div>
          )}

          {/* Bottom Floating Controls Bar */}
          <div className="meet-controls-dock">
            <button
              className={`meet-control-circle ${isAudioMuted ? 'muted' : ''}`}
              onClick={toggleAudio}
              title={isAudioMuted ? 'Turn on microphone' : 'Mute microphone'}
            >
              {isAudioMuted ? <AudioMutedOutlined /> : <AudioOutlined />}
            </button>

            <button
              className={`meet-control-circle ${isVideoOff ? 'off' : ''}`}
              onClick={toggleVideo}
              title={isVideoOff ? 'Turn on camera' : 'Turn off camera'}
            >
              <VideoCameraOutlined />
            </button>

            <button
              className={`meet-control-circle ${isScreenSharing ? 'active-share' : ''}`}
              onClick={toggleScreenShare}
              title={isScreenSharing ? 'Stop screen sharing' : 'Share your screen'}
            >
              <DesktopOutlined />
            </button>

            <button
              className={`meet-control-circle ${activeDrawer === 'chat' ? 'active-drawer' : ''}`}
              onClick={() => toggleDrawer('chat')}
              title="Open Chat"
            >
              <MessageOutlined />
              {unreadChatCount > 0 && <span className="meet-dock-badge">{unreadChatCount}</span>}
            </button>

            <button
              className={`meet-control-circle ${activeDrawer === 'participants' ? 'active-drawer' : ''}`}
              onClick={() => toggleDrawer('participants')}
              title="View Attendees"
            >
              <TeamOutlined />
              {isHost && waitingUsers.length > 0 && (
                <span className="meet-dock-badge">{waitingUsers.length}</span>
              )}
            </button>



            <button
              className="meet-control-circle hangup"
              onClick={handleLeaveMeeting}
              title="Leave call"
            >
              <PhoneOutlined className="meet-hangup-icon" />
            </button>
          </div>
        </div>

        {/* Side Drawer Panel (Chat / Attendees) */}
        {activeDrawer && (
          <aside className="meet-sidebar-drawer">
            <div className="meet-drawer-header">
              <div className="meet-drawer-title-group">
                {activeDrawer === 'chat' ? (
                  <>
                    <MessageOutlined /> <span>In-Meeting Messages</span>
                  </>
                ) : (
                  <>
                    <TeamOutlined /> <span>Meeting Attendees</span>
                  </>
                )}
              </div>
              <button
                className="meet-drawer-close-btn"
                onClick={() => setActiveDrawer(null)}
                aria-label="Close Drawer"
              >
                <CloseOutlined />
              </button>
            </div>

            {/* In-Call Chat Drawer */}
            {activeDrawer === 'chat' && (
              <div className="meet-drawer-content meet-chat-section">
                <div className="meet-chat-history">
                  <div className="meet-chat-notice">
                    <SafetyCertificateOutlined /> Messages are automatically stored in HRMS and available after the meeting.
                  </div>

                  {chatMessages.length === 0 ? (
                    <div className="meet-chat-empty">
                      <p>No messages yet.</p>
                      <span>Send a message to everyone in this meeting!</span>
                    </div>
                  ) : (
                    chatMessages.map((msg, index) => {
                      const isMe =
                        msg.senderName === currentUserName ||
                        msg.senderEmployeeId === currentEmployeeId;
                      const timeStr = msg.sentAt
                        ? new Date(msg.sentAt).toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit'
                          })
                        : '';

                      return (
                        <div
                          key={msg.id || index}
                          className={`meet-chat-bubble ${isMe ? 'is-me' : 'is-other'}`}
                        >
                          <div className="meet-chat-sender-info">
                            <span className="meet-chat-author">{isMe ? 'You' : msg.senderName}</span>
                            <span className="meet-chat-time">{timeStr}</span>
                          </div>
                          <div className="meet-chat-text">{msg.message}</div>
                        </div>
                      );
                    })
                  )}
                  <div ref={chatBottomRef} />
                </div>

                <form className="meet-chat-input-bar" onSubmit={handleSendMessage}>
                  <input
                    type="text"
                    placeholder="Send a message to everyone..."
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    className="meet-chat-input"
                  />
                  <button
                    type="submit"
                    className="meet-chat-send-btn"
                    disabled={!inputMessage.trim() || sendingChat}
                  >
                    <SendOutlined />
                  </button>
                </form>
              </div>
            )}

            {/* Attendees Drawer */}
            {activeDrawer === 'participants' && (
              <div className="meet-drawer-content meet-participants-section">
                {/* Host Waiting Room Section */}
                {isHost && waitingUsers.length > 0 && (
                  <div className="meet-waiting-list-section">
                    <div className="meet-waiting-list-header">
                      <span>Waiting to Join ({waitingUsers.length})</span>
                    </div>
                    {waitingUsers.map((w) => (
                      <div key={w.socketId} className="meet-waiting-row">
                        <div className="meet-waiting-user-meta">
                          <strong>{w.userName}</strong>
                          {w.employeeId && <small>Emp: {w.employeeId}</small>}
                        </div>
                        <div className="meet-waiting-row-actions">
                          <button
                            className="meet-btn-admit"
                            onClick={() => handleAdmitUser(w.socketId)}
                          >
                            Admit
                          </button>
                          <button
                            className="meet-btn-deny"
                            onClick={() => handleDenyUser(w.socketId)}
                          >
                            Deny
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* MS Teams-Style Call Pull Section */}
                <div className="meet-pull-section">
                  <div className="meet-pull-header">
                    <span>Invite someone to call</span>
                  </div>
                  <div className="meet-pull-search-wrap">
                    <SearchOutlined className="meet-pull-search-icon" />
                    <input
                      type="text"
                      className="meet-pull-search-input"
                      placeholder="Search employee name or email..."
                      value={pullSearch}
                      onChange={(e) => setPullSearch(e.target.value)}
                    />
                  </div>

                  {filteredInvitees.length > 0 && (
                    <div className="meet-pull-list">
                      {filteredInvitees.slice(0, 8).map((emp) => {
                        const empKey = `emp_${emp.id || emp.employeeId}`;
                        const userKey = emp.userId ? `user_${emp.userId}` : null;
                        const codeKey = emp.employeeCode ? `code_${emp.employeeCode}` : null;

                        const callState =
                          callingUsersMap[empKey] ||
                          (userKey && callingUsersMap[userKey]) ||
                          (codeKey && callingUsersMap[codeKey]);

                        const isCalling = callState === true;
                        const isDeclined = callState === 'declined';
                        return (
                          <div key={emp.id} className="meet-pull-item">
                            <div className="meet-pull-avatar">
                              {emp.name ? emp.name.charAt(0).toUpperCase() : 'E'}
                            </div>
                            <div className="meet-pull-meta">
                              <span className="meet-pull-name">{emp.name}</span>
                              <span className="meet-pull-sub">
                                {emp.employeeCode ? `${emp.employeeCode} • ` : ''}
                                {emp.email || 'Employee'}
                              </span>
                            </div>
                            <button
                              type="button"
                              className={`meet-pull-action-btn ${isCalling ? 'is-calling' : ''} ${isDeclined ? 'is-declined' : ''}`}
                              onClick={() => handlePullUserToCall(emp)}
                              disabled={isCalling || isDeclined}
                              title={isDeclined ? `${emp.name} declined the call` : 'Call and pull directly into this meeting'}
                            >
                              {isDeclined ? (
                                <>
                                  <CloseOutlined className="meet-pull-phone-icon" />
                                  <span>Declined</span>
                                </>
                              ) : (
                                <>
                                  <PhoneOutlined className="meet-pull-phone-icon" />
                                  <span>{isCalling ? 'Calling...' : 'Ask to join'}</span>
                                </>
                              )}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className="meet-participants-summary">
                  In This Call: <strong>{totalInCall}</strong>
                </div>

                <div className="meet-participants-list">
                  {/* You */}
                  <div className="meet-participant-row is-me">
                    <div className="meet-participant-avatar">
                      {currentUserName ? currentUserName.charAt(0).toUpperCase() : 'U'}
                    </div>
                    <div className="meet-participant-details">
                      <span className="meet-participant-name">{currentUserName} (You)</span>
                      <span className="meet-participant-role">{isHost ? 'Host' : 'Participant'}</span>
                    </div>
                    <span className="meet-status-dot online" title="Active in call" />
                  </div>

                  {/* Remote Connected Peers */}
                  {remotePeerList.map((p) => (
                    <div key={p.socketId} className="meet-participant-row">
                      <div className="meet-participant-avatar">
                        {p.userName ? p.userName.charAt(0).toUpperCase() : 'U'}
                      </div>
                      <div className="meet-participant-details">
                        <span className="meet-participant-name">{p.userName}</span>
                        <span className="meet-participant-role">
                          {p.employeeId ? `Emp: ${p.employeeId}` : 'Participant'}
                        </span>
                      </div>
                      <span className="meet-status-dot online" title="Active in call" />

                      {/* Host can kick remote participant */}
                      {isHost && (
                        <PermissionNativeButton permissionAction="delete"
                          className="meet-kick-btn"
                          onClick={() => handleKickParticipant(p.socketId, p.userName)}
                          title={`Remove ${p.userName} from meeting`}
                        >
                          <DeleteOutlined className="meet-delete-msg-icon" />
                        </PermissionNativeButton>
                      )}
                    </div>
                  ))}

                  {/* All DB Logged Attendees if any left */}
                  {participantsList
                    .filter(
                      (p) =>
                        p.participantName !== currentUserName &&
                        !remotePeerList.some((rp) => rp.userName === p.participantName)
                    )
                    .map((p, idx) => (
                      <div key={p.id || idx} className="meet-participant-row">
                        <div className="meet-participant-avatar">
                          {p.participantName ? p.participantName.charAt(0).toUpperCase() : 'U'}
                        </div>
                        <div className="meet-participant-details">
                          <span className="meet-participant-name">{p.participantName}</span>
                          <span className="meet-participant-role">
                            {p.leftAt ? 'Left Call' : 'Participant'}
                          </span>
                        </div>
                        <span
                          className={`meet-status-dot ${p.leftAt ? 'offline' : 'online'}`}
                          title={p.leftAt ? 'Left call' : 'Active'}
                        />
                      </div>
                    ))}
                </div>
              </div>
            )}
          </aside>
        )}
      </div>
    </div>
  );
}
